// @warp-css;
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { classNames } from "@chbphone55/classnames";
import { css, html, LitElement } from "lit";
import { property } from "lit/decorators.js";
import { FormControlMixin } from "@open-wc/form-control";
import { reset } from "../styles";
import { styles } from "./styles";
/**
 * Box is a layout component used for separating content areas on a page.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/box/frameworks/elements)
 */
class WarpBox extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        /**
         * Makes the box bleed to the container edge.
         * Extends the box into the horizontal gutter on narrow screens. Adds negative horizontal margins and square side corners below the small breakpoint, then restores the normal margins and rounded corners from the small breakpoint and up.
         */
        this.bleed = false;
        /**
         * Shows the box as a bordered surface.
         * Use this when the content needs a clear visual boundary from the surrounding page.
         */
        this.bordered = false;
        /**
         * Shows the box with information styling.
         * Use this for supporting informational content that should be visually separated from the surrounding page.
         */
        this.info = false;
        /**
         * Shows the box with neutral styling.
         * Use this for quiet grouped content that needs a background without strong emphasis.
         */
        this.neutral = false;
        /**
         * ARIA role for the box wrapper.
         * Defaults to a role of `region`. Set `role="none"` to override this behaviour for purely visual grouping, or set a specific role when the box has a clearer semantic purpose.
         */
        this.role = "region";
    }
    // Slotted elements remain in lightDOM which allows for control of their style outside of shadowDOM.
    // ::slotted([Simple Selector]) confirms to Specificity rules, but (being simple) does not add weight to lightDOM skin selectors,
    // so never gets higher Specificity. Thus in order to overwrite style linked within shadowDOM, we need to use !important.
    // https://stackoverflow.com/a/61631668
    static { this.styles = [
        reset,
        styles,
        css `
			:host {
				display: block;
			}
			::slotted(:last-child) {
				margin-bottom: 0px !important;
			}
		`,
    ]; }
    connectedCallback() {
        super.connectedCallback();
        this.internals.role = this.role;
    }
    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (_changedProperties.has("role")) {
            this.internals.role = this.role;
        }
    }
    /** @internal */
    get _class() {
        return classNames([
            "group block relative break-words last-child:mb-0 p-16 rounded-8",
            this.bleed && "-mx-16 sm:mx-0 rounded-l-0 rounded-r-0 sm:rounded-8",
            this.info && "s-bg-info-subtle",
            this.neutral && "s-surface-sunken",
            this.bordered && "border-2 s-border s-bg",
        ]);
    }
    render() {
        return html `
			<div class="${this._class}">
				<slot></slot>
			</div>
		`;
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], WarpBox.prototype, "bleed", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpBox.prototype, "bordered", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpBox.prototype, "info", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpBox.prototype, "neutral", void 0);
__decorate([
    property({ type: String, reflect: true, useDefault: true })
], WarpBox.prototype, "role", void 0);
if (!customElements.get("w-box")) {
    customElements.define("w-box", WarpBox);
}
export { WarpBox };
//# sourceMappingURL=box.js.map